import type { WebPage } from 'schema-dts'
import { useCoreConfig, useDayjs, useRoute, useSeoSchema, useSiteUrl } from '#imports'

export interface ArticleSchemaInput {
    type: 'BlogPosting' | 'NewsArticle'
    headline: string
    description?: string | null
    /** Absolute url. */
    image?: string | null
    datePublished: string
    dateModified?: string
}

export interface UseArticleSchemaReturn {
    addArticle: (input: ArticleSchemaInput) => void
    addAboutPage: () => void
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/

/**
 * Jurisdiction-local time with offset. Shared by the schema and `<time datetime>`
 * so the two always carry the same string.
 */
export function toSchemaDate(value: string | Date): string {
    const dayjs = useDayjs()
    return (typeof value === 'string' && DATE_ONLY.test(value) ? dayjs.tz(value) : dayjs(value).tz()).format()
}

/**
 * `updatedAt` as a schema date, only when it falls on a later calendar day than `publishedAt`
 * in the jurisdiction timezone. Otherwise the content counts as unedited.
 */
export function toDateModified(publishedAt: string, updatedAt?: string | null): string | undefined {
    if (!updatedAt) return undefined

    const modified = toSchemaDate(updatedAt)
    return modified.slice(0, 10) > toSchemaDate(publishedAt).slice(0, 10) ? modified : undefined
}

/**
 * Emits the page's `BlogPosting`/`NewsArticle` node, authored and published by the seoGraph
 * Organization, or turns the seoGraph `WebPage` into an `AboutPage`. No-op on the client and
 * without a site url.
 */
export const useArticleSchema = (): UseArticleSchemaReturn => {
    const seoSchema = useSeoSchema()
    const siteUrl = useSiteUrl()
    const route = useRoute()
    const { locale } = useCoreConfig()

    function addArticle({ type, headline, description, image, datePublished, dateModified }: ArticleSchemaInput) {
        if (import.meta.client) return

        const origin = siteUrl('')
        const pageUrl = siteUrl(route.path)
        if (!origin || !pageUrl) return

        const organization = { '@id': `${origin}/#organization` }

        seoSchema.add({
            '@type': type,
            headline,
            ...(description && { description }),
            ...(image && { image }),
            datePublished,
            ...(dateModified && { dateModified }),
            'author': organization,
            'publisher': organization,
            'mainEntityOfPage': { '@id': pageUrl },
            'inLanguage': locale
        })
    }

    function addAboutPage() {
        if (import.meta.client) return

        const origin = siteUrl('')
        const pageUrl = siteUrl(route.path)
        if (!origin || !pageUrl) return

        const webPage = seoSchema.state.value.find((node): node is Exclude<WebPage, string> =>
            typeof node === 'object' && (node as { '@id'?: unknown })['@id'] === pageUrl)
        if (!webPage) return

        Object.assign(webPage, {
            '@type': 'AboutPage',
            'mainEntity': { '@id': `${origin}/#organization` },
        })
    }

    return { addArticle, addAboutPage }
}
